import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import { Alert, Box, Button, Link, Stack, TextField } from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { acquisitionApi } from "../../api/acquisition";
import {
  ACQUISITION_EXPERIENCE_VERSION,
  acquisitionCampaign,
  acquisitionDeviceMetadata,
  acquisitionEventId,
  acquisitionSessionId,
  acquisitionVisitorId,
  saveAcquisitionDraft,
} from "../../utils/acquisitionFunnel";
import {
  AuthPageShell,
  AuthPasswordField,
  authLinkSx,
  authPrimaryButtonSx,
} from "../../components/dashboard/auth/AuthFormShell";

export default function RegisterPage() {
  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = String(searchParams.get("ref") || "").trim().toUpperCase();
  const entryPoint = String(searchParams.get("entry") || "register-direct").trim().slice(0, 80) || "register-direct";
  const loginHref = referralCode ? `/login?ref=${encodeURIComponent(referralCode)}` : "/login";
  const sessionId = React.useMemo(() => acquisitionSessionId(), []);
  const visitorId = React.useMemo(() => acquisitionVisitorId(), []);
  const [form, setForm] = React.useState({ name: "", email: "", password: "" });
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    // BLOCK 4 · Direct-registration acquisition bridge.
    // STATUS: STATIC_PASS · STAGING_PENDING.
    // Registration can be reached from the navbar or directly from an external
    // link. Always ensure there is a prospect session so a successful account can
    // be attributed even when the diagnostic funnel was intentionally skipped.
    const campaign = acquisitionCampaign(searchParams);
    saveAcquisitionDraft({ sessionId, visitorId, referralCode, entryPoint, campaign, experienceVersion: ACQUISITION_EXPERIENCE_VERSION });
    acquisitionApi.startSession({
      sessionId,
      visitorId,
      entryPoint,
      referralCode,
      campaign,
      audience: "unknown",
      experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
    }).catch(() => null);
  }, [entryPoint, referralCode, searchParams, sessionId, visitorId]);

  React.useEffect(() => {
    // Resolve authenticated test traffic after the immediate capture above.
    if (authLoading) return;
    const campaign = acquisitionCampaign(searchParams);
    acquisitionApi.startSession({
      sessionId,
      visitorId,
      entryPoint,
      referralCode,
      campaign,
      audience: user ? "authenticated" : "anonymous",
      experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
    }).catch(() => null);
  }, [authLoading, entryPoint, referralCode, searchParams, sessionId, user, visitorId]);

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const name = String(form.name || "").trim();
    const email = String(form.email || "").trim().toLowerCase();

    if (!name) {
      setError("Ingresá tu nombre.");
      return;
    }
    if (!email || !email.includes("@")) {
      setError("Ingresá un email válido.");
      return;
    }
    if (form.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // BLOCK 4 · The submit intent and the account creation share the same
      // session id. Analytics is best-effort and never blocks registration.
      await acquisitionApi.track({
        action: "acquisition_registration_started",
        sessionId,
        visitorId,
        eventId: acquisitionEventId("registration"),
        metadata: {
          stepKey: "account",
          stepIndex: 8,
          totalSteps: 9,
          progressPct: 90,
          entryPoint,
          audience: authLoading ? "unknown" : user ? "authenticated" : "anonymous",
          experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
          ...acquisitionDeviceMetadata(),
        },
      }).catch(() => null);

      const data = await register({
        name,
        email,
        password: form.password,
        referralCode: referralCode || undefined,
        prospectSessionId: sessionId,
      });

      // El backend ya creó la sesión. Entramos directo al panel aunque el
      // email todavía no esté verificado.
      navigate("/dashboard", {
        replace: true,
        state: {
          onboardingMessage: data?.onboarding?.requiresEmailVerification
            ? "Cuenta creada. Podés verificar tu email cuando quieras."
            : data?.onboarding?.message,
        },
      });
    } catch (err) {
      setError(err.message || "No se pudo crear la cuenta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      title="Crear cuenta"
      subtitle="Completá tus datos para empezar."
      helpLabel="¿Qué pasa después?"
      help={
        <>
          Al crear la cuenta entrás directamente a WebProfit. Te enviaremos un código para verificar tu email, pero podés hacerlo después sin bloquear el acceso. Si llegaste con un referido, el código se conserva durante este registro.
        </>
      }
      footer={
        <>
          ¿Ya tenés cuenta?{" "}
          <Link component={RouterLink} to={loginHref} sx={authLinkSx}>
            Ingresar
          </Link>
        </>
      }
    >
      {referralCode ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Referido: <strong>{referralCode}</strong>
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={1.5}>
          <TextField
            label="Nombre"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            fullWidth
            disabled={loading}
            autoFocus
            autoComplete="name"
            inputProps={{ autoCapitalize: "words" }}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            fullWidth
            disabled={loading}
            autoComplete="email"
            inputProps={{ inputMode: "email", autoCapitalize: "none", spellCheck: "false" }}
          />
          <AuthPasswordField
            label="Contraseña"
            name="password"
            value={form.password}
            onChange={handleChange}
            autoComplete="new-password"
            disabled={loading}
            helperText="Mínimo 8 caracteres."
          />
          <Button type="submit" disabled={loading} variant="contained" size="large" startIcon={loading ? <WebProfitSpinner size={17} /> : undefined} sx={authPrimaryButtonSx}>
            {loading ? "Creando..." : "Crear cuenta"}
          </Button>
        </Stack>
      </Box>
    </AuthPageShell>
  );
}
